import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value?: number;
  onValueChange: (value: number | undefined) => void;
  /** Optional capture categories show zero as blank and clearing writes numeric zero. */
  blankAsZero?: boolean;
};

const group = (raw: string) => {
  const [whole, cents] = raw.split('.');
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + (cents === undefined ? '' : `.${cents}`);
};
const amount = (raw: string) => ['', '-', '.', '-.'].includes(raw) ? undefined : Number(raw);

/** Group digits for display only; form state and API payloads keep numeric rand amounts. */
const MoneyInput = forwardRef<HTMLInputElement, Props>(function MoneyInput({ value, onValueChange, onBlur, onKeyDown, blankAsZero = false, ...props }, ref) {
  const [draft, setDraft] = useState<{ raw: string; value?: number }>();
  const input = useRef<HTMLInputElement | null>(null);
  const caret = useRef<number | undefined>(undefined);
  const raw = draft && Object.is(draft.value, value) ? draft.raw : value == null || !Number.isFinite(value) || (blankAsZero && value === 0) ? '' : String(value);
  const display = group(raw);

  useLayoutEffect(() => {
    const numeric = amount(raw);
    input.current?.setCustomValidity(raw && numeric === undefined ? 'Enter a valid amount.'
      : numeric != null && props.min != null && numeric < Number(props.min) ? `Amount must be at least ${props.min}.`
      : numeric != null && props.max != null && numeric > Number(props.max) ? `Amount must not exceed ${props.max}.` : '');
    if (caret.current !== undefined && input.current === document.activeElement) {
      input.current?.setSelectionRange(caret.current, caret.current);
    }
    caret.current = undefined;
  });

  const change = (text: string, position: number) => {
    const normalize = (part: string) => part.replace(/^\s*R\s*/i, '').replace(/\s/g, '').replace(',', '.');
    const next = normalize(text);
    // Reject malformed or over-precision input rather than silently changing its amount.
    if (!/^-?\d*(?:\.\d{0,2})?$/.test(next) || (amount(next) !== undefined && !Number.isFinite(Number(next)))) return;
    const nextValue = blankAsZero ? amount(next) ?? 0 : amount(next), formatted = group(next);
    const before = normalize(text.slice(0, position)).length;
    let offset = 0, characters = 0;
    while (offset < formatted.length && characters < before) {
      if (formatted[offset] !== ' ') characters++;
      offset++;
    }
    caret.current = offset;
    setDraft({ raw: next, value: nextValue });
    onValueChange(nextValue);
  };

  return <input {...props} type="text" inputMode="decimal" value={display}
    ref={element => { input.current = element; if (typeof ref === 'function') ref(element); else if (ref) ref.current = element; }}
    onChange={event => change(event.target.value, event.target.selectionStart ?? event.target.value.length)}
    onBlur={event => { setDraft(undefined); onBlur?.(event); }}
    onKeyDown={event => {
      onKeyDown?.(event);
      if (event.defaultPrevented || props.readOnly || props.disabled || event.ctrlKey || event.metaKey || event.altKey) return;
      const start = event.currentTarget.selectionStart ?? 0, end = event.currentTarget.selectionEnd ?? 0;
      // Backspace/Delete beside a grouping space should remove a digit, not reinsert the space.
      if (start === end && event.key === 'Backspace' && display[start - 1] === ' ') {
        event.preventDefault(); change(display.slice(0, start - 2) + display.slice(start), start - 2);
      } else if (start === end && event.key === 'Delete' && display[start] === ' ') {
        event.preventDefault(); change(display.slice(0, start) + display.slice(start + 2), start);
      }
    }} />;
});

export default MoneyInput;
